import { LazyFallback } from "@msuite/ui/loading-screen";
import { Panels } from "@msuite/ui/panels";
import { lazy, Suspense } from "react";
import { useReturnOrder } from "@/hooks/use-return-order";
import { ReturnOrderInformation } from "./return-order-information";
import { ReturnOrderSheet } from "./return-order-sheet";
import { ReturnOrderVehicles } from "./return-order-vehicles";

const Notes = lazy(() => import("@/components/notes"));

/** Props Interface */
interface ReturnOrderGeneralProps {
	returnOrderId: string;
}

/**
 * What the run is, who is fetching it, and the paper that goes in the cab — with the run's note
 * thread beside it.
 *
 * The notes take the wider column because they are the part that grows: the cards on the left state
 * facts that fit in a screen and stay that size, while a run being argued over at the gate
 * accumulates.
 *
 * `parent` is the run itself. Reading and writing a note follow the parent's own read right, and
 * `returnOrderNotes` answers to `Stock` — one right over the whole warehouse, with no reach that
 * narrows it to a run of one's own, so the row adds nothing the category did not already decide.
 * It is passed anyway, because leaving it out is what makes a narrower rule silently unanswerable
 * the day one is added.
 */
export function ReturnOrderGeneral({ returnOrderId }: ReturnOrderGeneralProps) {
	/** Query */
	const { returnOrder } = useReturnOrder(returnOrderId);

	/** Render */
	return (
		<Panels>
			<Panels.Panel
				flex={1}
				className="bottom-space"
			>
				<ReturnOrderInformation returnOrderId={returnOrderId} />
				<ReturnOrderVehicles returnOrderId={returnOrderId} />
				<ReturnOrderSheet returnOrderId={returnOrderId} />
			</Panels.Panel>
			<Panels.Panel flex={2}>
				<Suspense fallback={<LazyFallback variant="inline" />}>
					<Notes
						notesTable="returnOrderNotes"
						identifier={returnOrderId}
						parent={returnOrder}
					/>
				</Suspense>
			</Panels.Panel>
		</Panels>
	);
}
